import { useState, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import { TIPO_COLORS, colorConBlanco, TIPO_ICONS, fmtFull, fmtHours, prodBand } from "../../utils";
import { AG_PROD_DEFAULT, AG_PROD_SEED, AG_ORIGENES, AG_ORIGEN_DEFAULT, agOrigen } from "../../constants";
import Campo from "../forms/Campo";
import { colorNombre, iconoNombre } from "../../colorNombre";

// Atajos del peso de productividad: cubren los casos reales sin obligar a
// afinar el slider al grado.
const PROD_PRESETS = [
  { v:100, lb:"Máxima",  hint:"Trabajo que es el objetivo del puesto" },
  { v:70,  lb:"Alta",    hint:"Aporta, pero es de apoyo" },
  { v:50,  lb:"Neutra",  hint:"Necesaria, no productiva por sí misma" },
  { v:30,  lb:"Baja",    hint:"Coste de coordinación" },
  { v:0,   lb:"Nula",    hint:"Tiempo perdido: interrupciones, esperas" },
];

export default function TipoEntradaModal({
  tipo = null, tiposData = [], usage = null,
  canDelete = true, canArchive = true, canEditProd = false, canVerIncidencias = false,
  onSave, onDelete, onClose,
}) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const isNew = !tipo?.id;
  const usedColors = tiposData.filter(t => t.name !== tipo?.name && t.color).map(t => t.color);

  const [name,  setName ] = useState(tipo?.name  || "");
  const [color, setColor] = useState(tipo?.color || TIPO_COLORS[0]);
  const [icon,  setIcon ] = useState(tipo?.icon || "");
  // Un tipo previo a la migración puede venir sin peso: cae al de la plantilla
  // si su nombre está en ella, y si no, a neutro.
  const [prod,  setProd ] = useState(
    tipo?.productividad ?? AG_PROD_SEED[tipo?.name] ?? AG_PROD_DEFAULT
  );
  // De donde saca sus tareas este tipo. Un tipo previo a la migracion llega sin
  // origen y se lee como "solo descripcion".
  const [origen, setOrigen] = useState(tipo?.id ? agOrigen(tipo) : AG_ORIGEN_DEFAULT);
  const [showIcons, setShowIcons] = useState(false);
  const [danger,    setDanger   ] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [saving,    setSaving   ] = useState(false);

  const count = usage?.count || 0;
  const hours = usage?.hours || 0;
  // El nombre es el vínculo con las entradas: renombrar arrastra las que ya existen
  const renaming = !isNew && name.trim() && name.trim() !== tipo.name;
  const dupName  = tiposData.some(t => t.id !== tipo?.id && t.name.toLowerCase() === name.trim().toLowerCase());
  const colorTaken = usedColors.includes(color);
  const valid = !!name.trim() && !dupName;

  const otherTipos = tiposData.filter(t => t.id !== tipo?.id && !t.archived);
  const band = prodBand(prod);

  const lbS = {fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"};
  const inS = {width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)"};
  const secS = {marginBottom:16};

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    // El peso solo viaja si quien edita puede cambiarlo: enviarlo siempre haría
    // que el backend rechazara con 403 cualquier cambio de color de un no-admin.
    // `origen` viaja siempre, a diferencia de `productividad`: es un campo del
    // cuaderno de cada uno, no una politica de empresa, asi que no hay 403.
    try { await onSave({ name: name.trim(), color, icon, origen,
                         ...(canEditProd ? { productividad: prod } : {}) }); }
    finally { setSaving(false); }
  };

  const runDelete = async (opts) => {
    if (saving) return;
    setSaving(true);
    try { await onDelete(opts); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-bg"
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.38)",backdropFilter:"blur(3px)",zIndex:160,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={onClose}>
      <div ref={cajaRef} role="dialog" aria-modal="true" className="modal-box"
        style={{background:"var(--surface)",borderRadius:16,boxShadow:"var(--shadow-lg)",width:"100%",maxWidth:520,padding:24,maxHeight:"90vh",overflowY:"auto"}}
        onClick={e=>e.stopPropagation()}>

        <h3 style={{margin:"0 0 4px",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",gap:8}}>
          <Icon name={isNew?"plus-circle":"pencil-square"} size={15} color="var(--accent)"/>
          {isNew ? "Nuevo tipo de entrada" : "Editar tipo de entrada"}
        </h3>
        <p style={{margin:"0 0 18px",fontSize:11.5,color:"var(--text3)",lineHeight:1.5}}>
          Los tipos agrupan las entradas del cuaderno y definen los colores del dashboard y del PDF.
        </p>

        {/* Vista previa */}
        <div style={{...secS,padding:"12px 14px",borderRadius:11,background:"var(--surface2)",border:"1px solid var(--border)"}}>
          <span style={{...lbS,marginBottom:8}}>Vista previa</span>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:9,background:color,marginBottom:8}}>
            {icon ? <Icon name={icon} size={13} color="#fff"/>
                  : <span style={{width:8,height:8,borderRadius:"50%",background:"rgba(255,255,255,.85)"}}/>}
            <span style={{fontSize:12.5,fontWeight:700,color:"#fff",flex:1}}>{name.trim()||"Sin nombre"}</span>
            <span style={{fontSize:10,background:"rgba(0,0,0,.2)",color:"#fff",padding:"2px 8px",borderRadius:20,fontWeight:600}}>{count}</span>
            <span style={{fontSize:14,fontWeight:800,color:"#fff"}}>{fmtHours(hours)}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,background:color+"18",border:`1px solid ${color}44`}}>
              {icon ? <Icon name={icon} size={10} color={color}/>
                    : <span style={{width:6,height:6,borderRadius:"50%",background:color}}/>}
              <span style={{fontSize:11,fontWeight:600,color}}>{name.trim()||"Sin nombre"}</span>
            </div>
            <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:20,background:band.color+"18",border:`1px solid ${band.color}44`,fontSize:10.5,fontWeight:700,color:band.color}}>
              <Icon name="graph-up-arrow" size={10} color={band.color}/>{prod}% · {band.label}
            </span>
            {origen!=="ninguno" && (
              <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:20,background:"var(--bg)",border:"1px solid var(--border)",fontSize:10.5,fontWeight:600,color:"var(--text3)"}}>
                <Icon name={AG_ORIGENES[origen].icon} size={10} color="var(--text3)"/>{AG_ORIGENES[origen].label}
              </span>
            )}
          </div>
        </div>

        {/* Nombre */}
        <div style={secS}>
          <Campo><label style={lbS}>Nombre</label>
          <input value={name} onChange={e=>setName(e.target.value)} autoFocus
            onKeyDown={e=>{if(e.key==="Enter")submit();}}
            placeholder="Ej: Reuniones, Interrupciones…" style={inS}/></Campo>
          {dupName && (
            <div style={{fontSize:11,color:"var(--red-text)",marginTop:5,display:"flex",alignItems:"center",gap:4}}>
              <Icon name="exclamation-triangle-fill" size={10} color="var(--red-text)"/>Ya existe un tipo con ese nombre
            </div>
          )}
          {renaming && count>0 && !dupName && (
            <div style={{fontSize:11,color:"var(--warn-text)",marginTop:5,display:"flex",alignItems:"center",gap:4,lineHeight:1.4}}>
              <Icon name="arrow-repeat" size={10} color="#a16207"/>
              Se renombrarán también las {count} entradas que usan «{tipo.name}»
            </div>
          )}
        </div>

        {/* Origen: de donde salen las tareas de este tipo */}
        <div style={secS}>
          <div style={lbS} id="te-origen-et">Asignación predeterminada</div>
          <p style={{margin:"0 0 9px",fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
            Es la que sale preseleccionada al crear una entrada de este tipo o al cronometrarla
            desde Inicio. Puedes cambiarla en cada entrada, y enlazar nunca es obligatorio:
            siempre puedes escribirla a mano.
          </p>
          <div role="group" aria-labelledby="te-origen-et" style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            {Object.entries(AG_ORIGENES).map(([k,o])=>{
              const on = origen===k;
              return (
                <button key={k} className="btn" onClick={()=>setOrigen(k)} title={o.hint}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",borderRadius:9,
                    cursor:"pointer",fontFamily:"var(--font)",fontSize:11.5,fontWeight:on?700:500,
                    border:"1px solid "+(on?color:"var(--border)"),
                    background:on?color+"14":"var(--surface2)",
                    color:on?color:"var(--text2)",transition:"all .13s"}}>
                  <Icon name={o.icon} size={13} color={on?color:"var(--text3)"}/>{o.label}
                </button>
              );
            })}
          </div>
          {origen==="incidencia" && !canVerIncidencias && (
            <div style={{fontSize:11,color:"var(--warn-text)",marginTop:7,display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.45}}>
              <Icon name="exclamation-triangle-fill" size={11} color="#a16207" style={{marginTop:2,flexShrink:0}}/>
              <span>El panel de incidencias es del equipo TIC: a ti el buscador te pedirá
                escribir la interrupción a mano.</span>
            </div>
          )}
        </div>

        {/* Color */}
        <div style={secS}>
          <Campo><label style={lbS}>Color</label>
          <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:9}}>
            {TIPO_COLORS.map(c=>{
              const on   = color===c;
              const used = usedColors.includes(c);
              return (
                <button key={c} className="btn" onClick={()=>setColor(c)}
                  title={used?`${colorNombre(c)} — ya usado por otro tipo`:colorNombre(c)}
                  aria-label={"Color "+colorNombre(c)+(used?", ya usado por otro tipo":"")} aria-pressed={on}
                  style={{width:26,height:26,borderRadius:"50%",background:c,cursor:"pointer",padding:0,position:"relative",
                    border:on?"3px solid var(--text)":"3px solid transparent",
                    opacity:used&&!on?.45:1,
                    transform:on?"scale(1.18)":"scale(1)",transition:"transform .13s"}}>
                  {used&&!on&&<span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:"#fff",fontWeight:800,lineHeight:1}}>·</span>}
                </button>
              );
            })}
            <label title="Color personalizado"
              style={{width:26,height:26,borderRadius:"50%",border:"2px dashed var(--border)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative",overflow:"hidden"}}>
              <Icon name="palette-fill" size={11} color="var(--text3)"/>
              <input type="color" aria-label="Color personalizado" value={color} onChange={e=>setColor(colorConBlanco(e.target.value))}
                style={{position:"absolute",inset:0,opacity:0,cursor:"pointer"}}/>
            </label>
          </div></Campo>
          {colorTaken && (
            <div style={{fontSize:11,color:"var(--warn-text)",display:"flex",alignItems:"center",gap:4}}>
              <Icon name="exclamation-triangle-fill" size={10} color="#a16207"/>
              Otro tipo ya usa este color — los gráficos serán más difíciles de leer
            </div>
          )}
        </div>

        {/* Icono */}
        <div style={secS}>
          <Campo><label style={lbS}>Icono</label>
          <button className="btn" onClick={()=>setShowIcons(p=>!p)}
            style={{display:"flex",alignItems:"center",gap:8,padding:"7px 11px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",cursor:"pointer",fontFamily:"var(--font)",fontSize:12,color:"var(--text2)"}}>
            {icon ? <Icon name={icon} size={14} color={color}/>
                  : <span style={{width:8,height:8,borderRadius:"50%",background:color}}/>}
            <span>{icon || "Sin icono (punto de color)"}</span>
            <Icon name={showIcons?"chevron-up":"chevron-down"} size={10} color="var(--text3)"/>
          </button></Campo>
          {showIcons && (
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:9,maxHeight:168,overflowY:"auto",padding:2}}>
              <button className="btn" onClick={()=>setIcon("")} title="Sin icono" aria-label="Sin icono" aria-pressed={!icon}
                style={{width:32,height:32,borderRadius:8,cursor:"pointer",padding:0,display:"flex",alignItems:"center",justifyContent:"center",
                  border:"2px solid "+(!icon?color:"var(--border)"),
                  background:!icon?color+"18":"var(--bg)"}}>
                <span style={{width:8,height:8,borderRadius:"50%",background:!icon?color:"var(--text3)"}}/>
              </button>
              {TIPO_ICONS.map(ic=>(
                <button key={ic} className="btn" onClick={()=>setIcon(ic)} title={iconoNombre(ic)} aria-label={"Icono "+iconoNombre(ic)} aria-pressed={icon===ic}
                  style={{width:32,height:32,borderRadius:8,cursor:"pointer",padding:0,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s",
                    border:"2px solid "+(icon===ic?color:"var(--border)"),
                    background:icon===ic?color+"18":"var(--bg)",
                    transform:icon===ic?"scale(1.08)":"scale(1)"}}>
                  <Icon name={ic} size={14} color={icon===ic?color:"var(--text3)"}/>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Productividad */}
        <div style={secS}>
          <div style={lbS}>Productividad de este tipo</div>
          <p style={{margin:"0 0 9px",fontSize:11,color:"var(--text3)",lineHeight:1.5}}>
            Cuánto valor aporta una hora dedicada a este tipo. El dashboard pondera las horas por
            este peso para calcular el KPI de Productividad.
          </p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
            {PROD_PRESETS.map(p=>{
              const on = prod === p.v;
              const pb = prodBand(p.v);
              return (
                <button key={p.v} className="btn" onClick={()=>canEditProd&&setProd(p.v)}
                  disabled={!canEditProd} title={p.hint}
                  style={{padding:"5px 11px",borderRadius:20,cursor:canEditProd?"pointer":"not-allowed",
                    fontSize:11,fontWeight:on?700:600,fontFamily:"var(--font)",
                    border:"1px solid "+(on?pb.color:"var(--border)"),
                    background:on?pb.color+"18":"var(--surface2)",
                    color:on?pb.color:"var(--text3)",
                    opacity:canEditProd?1:.6,transition:"all .13s"}}>
                  {p.lb} <span style={{opacity:.7,fontWeight:600}}>{p.v}</span>
                </button>
              );
            })}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:11}}>
            <input aria-label="Productividad de este tipo, de 0 a 100" type="range" min={0} max={100} step={5} value={prod}
              disabled={!canEditProd}
              onChange={e=>setProd(parseInt(e.target.value,10))}
              style={{flex:1,accentColor:band.color,cursor:canEditProd?"pointer":"not-allowed",opacity:canEditProd?1:.6}}/>
            <span style={{minWidth:52,textAlign:"right",fontSize:17,fontWeight:800,color:band.color}}>{prod}%</span>
          </div>
          {!canEditProd && (
            <div style={{fontSize:11,color:"var(--text3)",marginTop:7,display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.45}}>
              <Icon name="shield-check" size={11} color="var(--text3)" style={{marginTop:2,flexShrink:0}}/>
              <span>
                El peso lo fija un administrador y es el mismo para toda la empresa: si cada persona
                ajustara el suyo, los porcentajes de unos y otros dejarían de ser comparables.
              </span>
            </div>
          )}
          {canEditProd && count>0 && (
            <div style={{fontSize:11,color:"var(--warn-text)",marginTop:7,display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.45}}>
              <Icon name="exclamation-triangle-fill" size={11} color="#a16207" style={{marginTop:2,flexShrink:0}}/>
              <span>Cambiar el peso recalcula el histórico: las {fmtHours(hours)} ya registradas en este tipo pasan a contar al nuevo valor.</span>
            </div>
          )}
        </div>

        {/* Uso real */}
        {!isNew && (
          <div style={{...secS,padding:"11px 13px",borderRadius:10,background:"var(--surface2)",border:"1px solid var(--border)",fontSize:11.5,color:"var(--text2)",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <Icon name="clock" size={12} color="var(--text3)"/>
            {count>0 ? (
              <span>
                <b style={{color:"var(--text)"}}>{count}</b> entrada{count===1?"":"s"} ·{" "}
                <b style={{color:"var(--text)"}}>{fmtHours(hours)}</b>
                {usage?.first && <> · desde <b style={{color:"var(--text)"}}>{fmtFull(usage.first)}</b></>}
                {usage?.last && usage.last!==usage.first && <> hasta <b style={{color:"var(--text)"}}>{fmtFull(usage.last)}</b></>}
              </span>
            ) : <span>Sin entradas registradas todavía</span>}
            {tipo?.archived && (
              <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:"var(--border)",color:"var(--text2)"}}>ARCHIVADO</span>
            )}
          </div>
        )}

        {/* Zona de riesgo */}
        {!isNew && (
          <div style={{marginBottom:18}}>
            {!danger ? (
              <button className="btn" onClick={()=>setDanger(true)}
                style={{background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"var(--font)",fontSize:11.5,color:"var(--text3)",display:"flex",alignItems:"center",gap:5}}>
                <Icon name="exclamation-triangle-fill" size={11} color="var(--text3)"/>Archivar o eliminar este tipo
              </button>
            ) : (
              <div style={{padding:"13px 14px",borderRadius:11,border:"1px solid var(--red-line)",background:"var(--red-bg)"}}>
                <div style={{fontSize:11.5,fontWeight:700,color:"var(--red-text)",marginBottom:9,display:"flex",alignItems:"center",gap:5}}>
                  <Icon name="exclamation-triangle-fill" size={12} color="#b91c1c"/>
                  {count>0 ? `Este tipo tiene ${count} entrada(s) — elige qué hacer con ellas` : "Eliminar este tipo"}
                </div>

                {/* Archivar: la opción segura cuando hay histórico */}
                {canArchive && !tipo.archived && (
                  <div style={{marginBottom:10}}>
                    <button className="btn" onClick={()=>runDelete({mode:"archive"})} disabled={saving}
                      style={{padding:"7px 12px",borderRadius:8,border:"1px solid #fcd34d",background:"var(--warn-bg)",color:"var(--warn-text)",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                      <Icon name="inbox" size={11} color="#a16207"/>Archivar
                    </button>
                    <div style={{fontSize:10.5,color:"#92400e",marginTop:4,lineHeight:1.4}}>
                      Desaparece de los desplegables pero conserva el histórico y sus horas en los informes. Recomendado.
                    </div>
                  </div>
                )}
                {tipo.archived && (
                  <div style={{marginBottom:10}}>
                    <button className="btn" onClick={()=>runDelete({mode:"unarchive"})} disabled={saving}
                      style={{padding:"7px 12px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",color:"var(--text2)",cursor:"pointer",fontSize:11.5,fontFamily:"var(--font)",fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
                      <Icon name="arrow-repeat" size={11} color="var(--text2)"/>Desarchivar
                    </button>
                  </div>
                )}

                {/* Reasignar + eliminar */}
                {count>0 && otherTipos.length>0 && (
                  <div style={{marginBottom:10}}>
                    <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
                      <select aria-label="Mover las entradas a otro tipo" value={reassignTo} onChange={e=>setReassignTo(e.target.value)}
                        style={{padding:"7px 9px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",fontSize:11.5,fontFamily:"var(--font)",color:"var(--text)",cursor:"pointer"}}>
                        <option value="">Mover las {count} entradas a…</option>
                        {otherTipos.map(t=><option key={t.id} value={t.name}>{t.name}</option>)}
                      </select>
                      <button className="btn" disabled={!reassignTo||saving}
                        onClick={()=>runDelete({mode:"reassign",reassignTo})}
                        style={{padding:"7px 12px",borderRadius:8,border:"none",background:"#b91c1c",color:"#fff",cursor:reassignTo?"pointer":"not-allowed",fontSize:11.5,fontFamily:"var(--font)",fontWeight:600,opacity:reassignTo?1:.45,display:"flex",alignItems:"center",gap:5}}>
                        <Icon name="chevron-right" size={11} color="#fff"/>Mover y eliminar
                      </button>
                    </div>
                    <div style={{fontSize:10.5,color:"#7f1d1d",marginTop:4}}>Las entradas conservan sus horas bajo el tipo destino.</div>
                  </div>
                )}

                {/* Eliminar dejando las entradas sin tipo */}
                <div>
                  <button className="btn" disabled={!canDelete||saving}
                    onClick={()=>runDelete({mode:"delete"})}
                    style={{padding:"7px 12px",borderRadius:8,border:"1px solid var(--red-line)",background:"var(--surface)",color:"var(--red-text)",cursor:canDelete?"pointer":"not-allowed",fontSize:11.5,fontFamily:"var(--font)",fontWeight:600,opacity:canDelete?1:.45,display:"flex",alignItems:"center",gap:5}}>
                    <Icon name="trash3" size={11} color="#b91c1c"/>
                    {count>0 ? "Eliminar y dejar las entradas sin tipo" : "Eliminar tipo"}
                  </button>
                  {!canDelete && (
                    <div style={{fontSize:10.5,color:"#7f1d1d",marginTop:4}}>No se puede eliminar el último tipo activo del cuaderno.</div>
                  )}
                  {canDelete && count>0 && (
                    <div style={{fontSize:10.5,color:"#7f1d1d",marginTop:4}}>Irán al grupo «Otros» y quedarán fuera de la distribución por tipo hasta reasignarlas.</div>
                  )}
                </div>

                <button className="btn" onClick={()=>setDanger(false)}
                  style={{background:"none",border:"none",cursor:"pointer",padding:0,marginTop:10,fontFamily:"var(--font)",fontSize:11,color:"var(--text3)"}}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button className="btn" onClick={onClose}
            style={{padding:"8px 16px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:13,background:"var(--surface2)",color:"var(--text2)",fontFamily:"var(--font)"}}>
            Cancelar
          </button>
          <BtnPrimary onClick={submit} disabled={!valid||saving}>
            {isNew ? "Crear tipo" : "Guardar cambios"}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
}
